import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  FileText, Users, Skull, Scroll, Sparkles, Map, Castle, BookOpen, UsersRound,
  Heart, Plus, UserCircle2, MapPin, Sword, Zap, Calendar, Edit3, Check, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sections = [
  { path: "chapters", label: "Campaign Story", icon: FileText, desc: "Chronicle your adventure chapter by chapter" },
  { path: "characters", label: "Characters", icon: Users, desc: "Track PCs, NPCs, and key figures" },
  { path: "characters?type=companion", label: "Companions", icon: Heart, desc: "Animal companions, familiars, and summoned allies", countKey: "companions" },
  { path: "creatures", label: "Bestiary", icon: Skull, desc: "Catalog monsters and creatures" },
  { path: "lore", label: "Lore", icon: Scroll, desc: "Record world history and legends" },
  { path: "deities", label: "Deities", icon: Sparkles, desc: "Document the pantheon and divine beings" },
  { path: "maps", label: "World Atlas", icon: Map, desc: "Upload and explore campaign maps" },
  { path: "locations", label: "Locations", icon: Castle, desc: "Detail cities, dungeons, and places" },
  { path: "notes", label: "Session Notes", icon: BookOpen, desc: "Personal and party session notes" },
  { path: "party", label: "The Party", icon: UsersRound, desc: "Manage party members and invites" },
];

const QUICK_CREATE_TYPES = [
  { type: "npc", label: "NPC", icon: UserCircle2, color: "text-emerald-400", accent: "bg-emerald-400/10 border-emerald-400/30 hover:bg-emerald-400/20" },
  { type: "location", label: "Location", icon: MapPin, color: "text-blue-400", accent: "bg-blue-400/10 border-blue-400/30 hover:bg-blue-400/20" },
  { type: "item", label: "Item", icon: Sword, color: "text-purple-400", accent: "bg-purple-400/10 border-purple-400/30 hover:bg-purple-400/20" },
  { type: "creature", label: "Creature", icon: Skull, color: "text-red-400", accent: "bg-red-400/10 border-red-400/30 hover:bg-red-400/20" },
  { type: "lore", label: "Lore", icon: Scroll, color: "text-cyan-400", accent: "bg-cyan-400/10 border-cyan-400/30 hover:bg-cyan-400/20" },
];

const RACES = ["Human", "Elf", "Dwarf", "Halfling", "Dragonborn", "Gnome", "Half-Elf", "Half-Orc", "Tiefling", "Other"];
const ITEM_TYPES = ["weapon", "armour", "potion", "scroll", "gem", "artifact", "misc"];
const RARITIES = ["common", "uncommon", "rare", "very_rare", "legendary"];
const LOC_TYPES = ["city", "town", "village", "dungeon", "wilderness", "ruin", "other"];

function QuickCreateDialog({ open, onOpenChange, type, campaignId, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const resetAndClose = () => { setForm({}); onOpenChange(false); };

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      let endpoint, payload;
      if (type === "npc") {
        endpoint = `${API}/npcs`;
        payload = { campaign_id: campaignId, name: form.name, race: form.race || "", profession: form.profession || "", description: form.description || "" };
      } else if (type === "location") {
        endpoint = `${API}/locations`;
        payload = { campaign_id: campaignId, name: form.name, location_type: form.location_type || "", description: form.description || "" };
      } else if (type === "item") {
        endpoint = `${API}/items`;
        payload = { campaign_id: campaignId, name: form.name, item_type: form.item_type || "misc", rarity: form.rarity || "common", description: form.description || "" };
      } else if (type === "creature") {
        endpoint = `${API}/creatures`;
        payload = { campaign_id: campaignId, name: form.name, creature_type: form.creature_type || "", challenge_rating: form.challenge_rating || "", description: form.description || "" };
      } else if (type === "lore") {
        endpoint = `${API}/lore`;
        payload = { campaign_id: campaignId, title: form.name, category: form.category || "general", content: form.description || "" };
      }
      await axios.post(endpoint, payload, { withCredentials: true });
      toast.success(`${type === "lore" ? "Lore" : form.name} created`);
      resetAndClose();
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally { setSaving(false); }
  };

  const meta = QUICK_CREATE_TYPES.find(q => q.type === type) || QUICK_CREATE_TYPES[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="quick-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <meta.icon className={`w-5 h-5 ${meta.color}`} />
            Quick Create {meta.label}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input
            placeholder={type === "lore" ? "Title" : "Name"}
            value={form.name || ""}
            onChange={e => setForm({ ...form, name: e.target.value })}
            data-testid="quick-create-name"
            autoFocus
          />

          {type === "npc" && (
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.race || ""} onValueChange={v => setForm({ ...form, race: v })}>
                <SelectTrigger data-testid="quick-create-race"><SelectValue placeholder="Race" /></SelectTrigger>
                <SelectContent>{RACES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Profession" value={form.profession || ""} onChange={e => setForm({ ...form, profession: e.target.value })} data-testid="quick-create-profession" />
            </div>
          )}

          {type === "location" && (
            <Select value={form.location_type || ""} onValueChange={v => setForm({ ...form, location_type: v })}>
              <SelectTrigger data-testid="quick-create-loc-type"><SelectValue placeholder="Location type" /></SelectTrigger>
              <SelectContent>{LOC_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          )}

          {type === "item" && (
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.item_type || "misc"} onValueChange={v => setForm({ ...form, item_type: v })}>
                <SelectTrigger data-testid="quick-create-item-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>{ITEM_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.rarity || "common"} onValueChange={v => setForm({ ...form, rarity: v })}>
                <SelectTrigger data-testid="quick-create-rarity"><SelectValue placeholder="Rarity" /></SelectTrigger>
                <SelectContent>{RARITIES.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace("_"," ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          {type === "creature" && (
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Creature type (e.g. Undead)" value={form.creature_type || ""} onChange={e => setForm({ ...form, creature_type: e.target.value })} data-testid="quick-create-creature-type" />
              <Input placeholder="CR (e.g. 5)" value={form.challenge_rating || ""} onChange={e => setForm({ ...form, challenge_rating: e.target.value })} data-testid="quick-create-cr" />
            </div>
          )}

          {type === "lore" && (
            <Select value={form.category || "general"} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="quick-create-lore-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                {["general","history","legend","artifact","event","prophecy","organization","secret"].map(c => (
                  <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Textarea
            placeholder="Brief description (optional)"
            value={form.description || ""}
            onChange={e => setForm({ ...form, description: e.target.value })}
            className="min-h-[60px] resize-none"
            data-testid="quick-create-description"
          />

          <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="quick-create-save">
            {saving ? "Creating..." : `Create ${meta.label}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const CampaignView = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [quickCreateType, setQuickCreateType] = useState(null);

  const fetchData = async () => {
    try {
      const campaignRes = await axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true });
      setCampaign(campaignRes.data);

      const countPromises = [
        axios.get(`${API}/campaigns/${campaignId}/chapters`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/characters`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/creatures`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/lore`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/deities`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/maps`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/locations`, { withCredentials: true }),
      ];

      const results = await Promise.all(countPromises);
      const allChars = results[1].data || [];
      setCounts({
        chapters: results[0].data.length,
        characters: allChars.filter(c => (c.character_type || "pc") === "pc" || c.character_type === "npc").length,
        companions: allChars.filter(c => c.character_type === "companion" || c.character_type === "summon").length,
        creatures: results[2].data.length,
        lore: results[3].data.length,
        deities: results[4].data.length,
        maps: results[5].data.length,
        locations: results[6].data.length,
      });
    } catch {
      toast.error("Failed to load campaign");
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [campaignId]);

  if (loading) {
    return (
      <Layout campaignName="Loading...">
        <div className="flex justify-center py-20"><div className="spinner" /></div>
      </Layout>
    );
  }

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="campaign-view">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-3xl md:text-4xl font-serif text-foreground mb-2">{campaign?.name}</h1>
            <p className="text-muted-foreground">{campaign?.description || "Begin your chronicle..."}</p>
          </div>
          <CampaignDateWidget campaign={campaign} onChange={(c) => setCampaign(prev => prev ? { ...prev, ...c } : prev)} />
        </div>

        {/* Quick Create Bar */}
        <div className="mb-8 p-3 rounded-sm border border-border bg-muted/5" data-testid="quick-create-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 mr-2">
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Quick Create</span>
            </div>
            {QUICK_CREATE_TYPES.map(qc => (
              <button
                key={qc.type}
                onClick={() => setQuickCreateType(qc.type)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-medium transition-all ${qc.accent} ${qc.color}`}
                data-testid={`quick-create-${qc.type}`}
              >
                <Plus className="w-3 h-3" />
                {qc.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((section) => (
            <Card
              key={section.path}
              className="bg-card border-border rounded-sm cursor-pointer campaign-card"
              onClick={() => navigate(`/campaign/${campaignId}/${section.path}`)}
              data-testid={`section-${section.path.replace(/\?.*/, "")}${section.countKey ? `-${section.countKey}` : ""}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                    <section.icon className="w-6 h-6 text-primary" />
                  </div>
                  <span className="text-2xl font-serif text-primary">
                    {counts[section.countKey || section.path.replace(/\?.*/, "")] || 0}
                  </span>
                </div>
                <h3 className="text-lg font-serif text-foreground mb-1">{section.label}</h3>
                <p className="text-sm text-muted-foreground">{section.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <QuickCreateDialog
        open={!!quickCreateType}
        onOpenChange={(v) => !v && setQuickCreateType(null)}
        type={quickCreateType || "npc"}
        campaignId={campaignId}
        onCreated={fetchData}
      />
    </Layout>
  );
};

export default CampaignView;

// ============================================================================
// CampaignDateWidget — in-game calendar (DM-editable, all-readable).
// ============================================================================
const CALENDARS = ["Gregorian", "Forgotten Realms (Calendar of Harptos)", "Eberron (Galifar)", "Greyhawk", "Dragonlance (Krynn)", "Custom / Homebrew"];

const CampaignDateWidget = ({ campaign, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draftDate, setDraftDate] = useState(campaign?.current_in_game_date || "");
  const [draftCal, setDraftCal] = useState(campaign?.calendar_name || "Gregorian");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraftDate(campaign?.current_in_game_date || "");
    setDraftCal(campaign?.calendar_name || "Gregorian");
  }, [campaign?.id]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/campaigns/${campaign.id}/in-game-date`, {
        current_in_game_date: draftDate,
        calendar_name: draftCal,
      }, { withCredentials: true });
      onChange?.({ current_in_game_date: res.data.current_in_game_date, calendar_name: res.data.calendar_name });
      setEditing(false);
      toast.success("In-game date updated");
    } catch { toast.error("Failed to save date"); }
    finally { setSaving(false); }
  };

  if (!campaign) return null;

  return (
    <div className="bg-muted/20 border border-border rounded-md px-3 py-2 min-w-[260px]" data-testid="campaign-date-widget">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
        <Calendar className="w-3 h-3" /> In-Game Date
      </div>
      {editing ? (
        <div className="space-y-1.5">
          <Input
            value={draftDate}
            onChange={(e) => setDraftDate(e.target.value)}
            placeholder="e.g. 15th of Mirtul, 1492 DR"
            className="h-7 text-sm bg-input border-border"
            data-testid="campaign-date-input"
          />
          <Select value={draftCal} onValueChange={setDraftCal}>
            <SelectTrigger className="h-7 text-xs bg-input border-border" data-testid="campaign-calendar-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CALENDARS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-1 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-6 text-[10px]" data-testid="campaign-date-cancel"><X className="w-3 h-3" /></Button>
            <Button size="sm" onClick={save} disabled={saving} className="h-6 text-[10px] bg-primary text-primary-foreground" data-testid="campaign-date-save"><Check className="w-3 h-3 mr-1" />Save</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground truncate" data-testid="campaign-date-display">{campaign.current_in_game_date || <span className="italic text-muted-foreground">Not set yet</span>}</p>
            <p className="text-[10px] text-muted-foreground truncate">{campaign.calendar_name || "Gregorian"}</p>
          </div>
          <button onClick={() => setEditing(true)} className="text-muted-foreground hover:text-primary flex-shrink-0" data-testid="campaign-date-edit">
            <Edit3 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};
